import { RouteObject, redirect } from "react-router-dom";
import Layout from "./components/Layout/Layout";
import { Report } from "./views/Report/Report";
import { AnalysisPage } from "./views/Analysis/Analysis";
import EventWithPublicFallback from "./views/Event/EventWithPublicFallback";
import Login from "./auth/Login";
import AuthCallback from "./auth/AuthCallback";
import ErrorPage from "./views/Error/ErrorPage";
import { CustomReportPage } from "./views/CustomReport/CustomReportPage";
import { GlobalReportLanding } from "./views/GlobalReport/GlobalReportLanding";
import { GlobalReportPage } from "./views/GlobalReport/GlobalReportPage";
import { ReportTypeEnum, RouteHandle, InsightType } from "./type/enum";
import { checkEnv } from "./helper/helper";
import WithActiveSubscription from "./components/WithActiveSubscription/WithActiveSubscription";
import SubscriptionExpired from "./views/SubscriptionExpired/SubscriptionExpired";
import InactiveUser from "./views/InactiveUser/InactiveUser";
import RefreshStatus from "./views/RefreshStatus/RefreshStatus";
import LinkedInCallback from "./auth/LinkedInCallback";
import NotFound from "./views/NotFound/NotFound";

const shortlinkUrl = import.meta.env.VITE_FOUNDRY_APP_SHORTLINK_URL;
const homeUrl = import.meta.env.VITE_FOUNDRY_APP_HOME_URL;
checkEnv(shortlinkUrl, "VITE_FOUNDRY_APP_SHORTLINK_URL");
checkEnv(homeUrl, "VITE_FOUNDRY_APP_HOME_URL");

const appRoutes: RouteObject[] = [
  {
    errorElement: <ErrorPage />,
    loader: () => {
      // We used to use a separate domain for shortlinks. If you enter through that domain, redirect to the main app domain instead.
      if (location.origin === shortlinkUrl) {
        throw redirect(
          homeUrl + location.pathname + location.search + location.hash,
        );
      }
    },
    children: [
      {
        // The short link domain used to use an extra '/link' part in the url. If we find this, redirect to the normal event url.
        path: "/link/event/:id",
        loader: ({ params }) => {
          throw redirect(`/event/${params.id}`);
        },
      },
      {
        element: <WithActiveSubscription />,
        children: [
          {
            element: <Layout />,
            children: [
              {
                path: "/",
                element: <Report reportType={ReportTypeEnum.Daily} />,
                handle: {
                  sidebarSection: InsightType.PersonalDailyBriefs,
                  pageTitle: "Insights",
                } satisfies RouteHandle,
              },
              {
                path: "/report/daily/:date",
                element: <Report reportType={ReportTypeEnum.Daily} />,
                handle: {
                  sidebarSection: InsightType.PersonalDailyBriefs,
                  pageTitle: "Insights",
                } satisfies RouteHandle,
              },
              {
                path: "/report/weekly/:date",
                element: <Report reportType={ReportTypeEnum.Weekly} />,
              },
              {
                path: "/event-analysis/:analysisId",
                element: <AnalysisPage />,
                handle: {
                  sidebarSection: InsightType.EventDeepDives,
                  pageTitle: "Event Analysis",
                } satisfies RouteHandle,
              },
              {
                path: "/topic-analysis/:analysisId",
                element: <AnalysisPage />,
                handle: {
                  sidebarSection: InsightType.TopicDeepDives,
                  pageTitle: "Topic Analysis",
                } satisfies RouteHandle,
              },
              {
                path: "/custom-report/:reportId",
                element: <CustomReportPage />,
                handle: {
                  sidebarSection: InsightType.CustomReports,
                  pageTitle: "Custom Report",
                } satisfies RouteHandle,
              },
              {
                path: "/global-research",
                element: <GlobalReportLanding />,
                handle: {
                  sidebarSection: InsightType.GlobalReports,
                  pageTitle: "Global Research",
                } satisfies RouteHandle,
              },
              {
                path: "/global-research/:reportSlug",
                element: <GlobalReportPage />,
                handle: {
                  sidebarSection: InsightType.GlobalReports,
                  pageTitle: "Global Research",
                } satisfies RouteHandle,
              },
              {
                path: "*",
                element: <NotFound />,
              },
            ],
            errorElement: <ErrorPage />,
          },
        ],
      },
      {
        path: "/event/:eventId/:activeTab?",
        element: <EventWithPublicFallback />,
        handle: { pageTitle: "Event" } satisfies RouteHandle,
        errorElement: <ErrorPage />,
      },
      {
        path: "/subscription-expired",
        element: <SubscriptionExpired />,
      },
      {
        path: "/inactive-user",
        element: <InactiveUser />,
      },
      {
        path: "/refresh-status",
        element: <RefreshStatus />,
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "auth-callback",
        element: <AuthCallback />,
      },
      {
        path: "linkedin-callback",
        element: <LinkedInCallback />,
      },
    ],
  },
];

export default appRoutes;
